import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { GitMerge } from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { Avatar } from "@/components/base/avatar/avatar";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import {
	EmptyValue,
	PhoneNumber,
	TableAddressDisplay,
} from "@/components/common";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import {
	SubOrgTableFilters,
	type SubOrgTableFiltersState,
} from "@/components/Molecules/FilterDrawer/SubOrgTableFilters";
import type { ColumnDef } from "@/components/Molecules/Table";
import { CustomTableWrapper } from "@/components/Molecules/Table/CustomTableWrapper";
import { TableSearchEmptyState } from "@/components/Molecules/Table/TableSearchEmptyState";
import CreateSubConModal from "@/components/Organisms/CreateSubConModal/CreateSubConModal";
import { PageHeader } from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { Illustration } from "@/components/shared-assets/illustrations";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useEntityActionAllowed } from "@/hooks/use-entity-action-allowed";
import { useDebounced } from "@/hooks/use-debounced";
import { useTableSettings } from "@/hooks/use-table-settings";
import {
	isClosedSubContractor,
	organizationQueryOptions,
	SUB_CONTRACTOR_STATUSES_INCLUDING_CLOSED,
	SubContractorStatusFilter,
	subContractorStatusesParam,
	type SubContractorListItem,
	useExportSubContractors,
} from "@/services/organization";
import {
	buildFilterParams,
	type FilterConfig,
} from "@/utils/build-filter-params";
import { downloadBlob, generateExportFilename } from "@/utils/download";
import { formatLongDate } from "@/utils/utils";

// Table settings constants
const TABLE_KEY = "orgAdmin.subContractors";
const DEFAULT_COLUMN_ORDER = [
	"name",
	"status",
	"location",
	"phone",
	"totalAssignedUserCount",
	"created",
	"lastUpdate",
];
const DEFAULT_SORTING = [{ id: "name", desc: false }];
const DEFAULT_PAGE_SIZE = 10;

const normalizeColumnOrder = (columnOrder?: string[]) => {
	if (!Array.isArray(columnOrder) || columnOrder.length === 0) {
		return [...DEFAULT_COLUMN_ORDER];
	}

	const allowedColumns = new Set(DEFAULT_COLUMN_ORDER);
	const sanitized = columnOrder.filter((columnId) =>
		allowedColumns.has(columnId),
	);
	const unique = Array.from(new Set(sanitized));
	const missingDefaults = DEFAULT_COLUMN_ORDER.filter(
		(columnId) => !unique.includes(columnId),
	);

	return [...unique, ...missingDefaults];
};

// Serializable filter state for persistence (excludes date ranges which are complex objects)
// Note: Date ranges are not persisted - they reset on page load
type PersistedFilters = Record<string, never>;
const DEFAULT_FILTERS: PersistedFilters = {};

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-contractors",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return z
			.object({
				mode: z.enum(["view", "edit"]).optional(),
				tab: z.enum(["details", "users"]).optional(),
				organizationId: z.string().optional(),
				subContractorId: z.string().optional(),
				filterOpen: z.boolean().optional(),
				create: z.enum(["sub-contractor"]).optional(),
			})
			.parse(search);
	},
});

function RouteComponent() {
	const navigate = useNavigate();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-contractors",
	});
	const [isOpen, setIsOpen] = useState(false);
	const canCreateSubContractor = useEntityActionAllowed({
		name: "CreateSubContractor",
		enabled: params.orgId.length > 0,
	});
	const openCreate = () => {
		setIsOpen(true);
		navigate({
			to: "/org-admin/$orgId/sub-contractors",
			params: { orgId: params.orgId },
			search: { create: "sub-contractor" },
		});
	};

	const organizationName = useQuery({
		...organizationQueryOptions.detail(params.orgId),
		select: (data) => data?.name,
	})?.data;

	return (
		<div className="space-y-0">
			<SubContractorsTableComponent
				onCreateClick={canCreateSubContractor.allowed ? openCreate : undefined}
			/>
			{canCreateSubContractor.allowed ? (
			<CreateSubConModal
				isOpen={isOpen}
				setIsOpen={setIsOpen}
				handleClose={() => setIsOpen(false)}
				organizationName={organizationName ?? ""}
			/>
			) : null}
		</div>
	);
}

function SubContractorsTableComponent({
	onCreateClick,
}: {
	onCreateClick?: () => void;
}) {
	const navigate = useNavigate();
	const { t } = useAppTranslation();
	const queryClient = useQueryClient();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-contractors",
	});

	// Pagination state
	const [pageSize, setPageSize] = useState(10);
	const [pageIndex, setPageIndex] = useState(0);
	const [tableOptionsHost, setTableOptionsHost] =
		useState<HTMLDivElement | null>(null);
	const [searchTerm, setSearchTerm] = useState("");
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [filters, setFilters] = useState<SubOrgTableFiltersState>({
		status: SubContractorStatusFilter.Active,
		createdDateRange: undefined,
		lastUpdateDateRange: undefined,
	});
	const [sortParams, setSortParams] = useState({
		"OrderBy.Fields": ["Name"],
		"OrderBy.Descending": false,
	});

	// Track if persisted settings have been applied
	const hasAppliedPersistedSettings = useRef(false);

	// Table settings persistence
	const {
		settings: tableSettings,
		isLoaded: settingsLoaded,
		saveSorting,
		saveColumnOrder,
		saveColumnVisibility,
		saveSearchTerm,
		saveFilters,
		savePageSize,
	} = useTableSettings<PersistedFilters>({
		tableKey: TABLE_KEY,
		defaultSorting: DEFAULT_SORTING,
		defaultColumnOrder: DEFAULT_COLUMN_ORDER,
		defaultSearchTerm: "",
		defaultFilters: DEFAULT_FILTERS,
		defaultPageSize: DEFAULT_PAGE_SIZE,
	});

	// Apply persisted settings (search, filters, page size) when config has loaded
	useEffect(() => {
		if (!settingsLoaded || hasAppliedPersistedSettings.current) return;
		hasAppliedPersistedSettings.current = true;

		if (tableSettings.searchTerm) {
			setSearchTerm(tableSettings.searchTerm);
		}
		if (tableSettings.filters) {
			setFilters((prev) => ({
				...prev,
			}));
		}
		if (
			Number.isFinite(tableSettings.pageSize) &&
			[10, 25, 50, 100].includes(tableSettings.pageSize)
		) {
			setPageSize(tableSettings.pageSize);
		}
	}, [
		settingsLoaded,
		tableSettings.searchTerm,
		tableSettings.filters,
		tableSettings.pageSize,
	]);

	// Sort field mapping - maps column IDs to API field names
	const sortFieldMapping: Record<string, string[]> = {
		name: ["Name"],
		address: ["Address.City", "Address.State"],
		phone: ["Phone"],
		totalAssignedUserCount: ["TotalAssignedUserCount"],
		created: ["CreatedAt"],
		lastUpdated: ["UpdatedAt"],
	};

	// Calculate the start index for the query (1-based indexing)
	const startIndex = pageIndex * pageSize + 1;
	const exportMutation = useExportSubContractors(params.orgId);
	const debouncedFilters = useDebounced(filters, 500);

	// Filter configuration for buildFilterParams utility
	const filterConfig: FilterConfig = {
		"createdDateRange.start": {
			paramName: "CreatedAfter",
			transform: "dateStart",
		},
		"createdDateRange.end": {
			paramName: "CreatedBefore",
			transform: "dateEnd",
			dateStartField: "createdDateRange.start",
		},
		"lastUpdateDateRange.start": {
			paramName: "LastUpdatedAfter",
			transform: "dateStart",
		},
		"lastUpdateDateRange.end": {
			paramName: "LastUpdatedBefore",
			transform: "dateEnd",
			dateStartField: "lastUpdateDateRange.start",
		},
	};

	const filterParams = useMemo(
		() => buildFilterParams(debouncedFilters, filterConfig),
		[debouncedFilters],
	);
	const statusFilter =
		debouncedFilters.status ?? SubContractorStatusFilter.Active;
	const statusParams = useMemo(
		() => ({
			Statuses: subContractorStatusesParam(statusFilter),
		}),
		[statusFilter],
	);

	// Get sub-contractor count
	const _subContractorCount = useQuery(
		organizationQueryOptions.subContractors.count(params.orgId, {
			SearchTerm: searchTerm,
			...filterParams,
			...statusParams,
			...sortParams,
		}),
	);

	const unFilteredSubContractorCount = useQuery(
		organizationQueryOptions.subContractors.count(params.orgId, {
			Statuses: SUB_CONTRACTOR_STATUSES_INCLUDING_CLOSED,
		}),
	);

	// Fetch sub-contractors with regular query, using pagination
	const allSubContractors = useQuery(
		organizationQueryOptions.subContractors.list(
			params.orgId,
			startIndex,
			pageSize,
			{
				SearchTerm: searchTerm,
				...filterParams,
				...statusParams,
				...sortParams,
			},
		),
	);

	// Use the data directly from the query
	const data = useMemo(() => {
		return allSubContractors.data ?? [];
	}, [allSubContractors.data]);

	// Calculate total page count from total sub-contractor count
	const pageCount = useMemo(() => {
		if (!_subContractorCount.data) return 0;
		return Math.ceil(_subContractorCount.data / pageSize);
	}, [_subContractorCount.data, pageSize]);

	// Handle refresh with loading state
	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			await allSubContractors.refetch();
		} finally {
			setIsRefreshing(false);
		}
	};

	const handleExport = (visibleColumns?: string[]) => {
		const totalCount = _subContractorCount.data ?? 0;
		if (totalCount === 0 || totalCount > 10000) return;

		const mappedColumns = visibleColumns?.flatMap((col) => {
			if (col === "address") {
				return [
					"address.street1",
					"address.street2",
					"address.city",
					"address.state",
					"address.country",
					"address.postalCode",
				];
			}
			return col;
		});

		exportMutation.mutate(
			{
				startIndex: 1,
				count: totalCount,
				params: {
					SearchTerm: searchTerm,
					...filterParams,
					...statusParams,
					...sortParams,
					columns: mappedColumns?.join(","),
				},
			},
			{
				onSuccess: (blob) => {
					downloadBlob(blob, generateExportFilename("sub_contractors"));
				},
			},
		);
	};

	// Handle row click to navigate to sub-contractor details
	const handleRowClick = (subContractor: SubContractorListItem) => {
		if (!subContractor.id) return;
		navigate({
			to: "/org-admin/$orgId/sub-contractors/$subConId/details",
			params: { orgId: params.orgId, subConId: subContractor.id },
			search: {
				mode: "view",
				organizationId: params.orgId,
				tab: "details",
			},
		});
	};

	// Handler for pagination changes from the table
	const handlePaginationChange = ({
		pageSize: newPageSize,
		pageIndex: newPageIndex,
	}: {
		pageSize: number;
		pageIndex: number;
	}) => {
		if (newPageSize !== pageSize) {
			setPageSize(newPageSize);
			setPageIndex(0);
			savePageSize(newPageSize);
		} else if (newPageIndex !== pageIndex) {
			setPageIndex(newPageIndex);
		}
	};

	const handleDebouncedSearchChange = (search: string) => {
		setSearchTerm(search);
		setPageIndex(0);
		// Save search term for persistence
		saveSearchTerm(search);
	};

	const handleFilterChange = (newFilters: SubOrgTableFiltersState) => {
		setFilters(newFilters);
		setPageIndex(0);
		// Save filters for persistence (only serializable fields)
		// Note: Date ranges are not persisted
		saveFilters({});
	};

	// Column definitions
	const subContractorColumns: ColumnDef<SubContractorListItem>[] = [
		{
			id: "name",
			accessorKey: "name",
			header: t("org-admin.sub-contractors.table.columns.name"),
			enableSorting: true,
			enableHiding: false, // Cannot hide the row header column
			meta: {
				headerProps: {
					isRowHeader: true,
				},
			},
			minSize: 200,
			cell: ({ row }) => {
				const subContractor = row.original;
				return (
					<span className="text-sm flex items-center">
						<Avatar
							className="mr-3 [&_svg]:text-fg-white"
							size="sm"
							placeholderIcon={GitMerge}
						/>
						<div className="flex flex-col">
							{subContractor.name ? `${subContractor.name}` : <EmptyValue />}
						</div>
					</span>
				);
			},
		},
		{
			id: "status",
			accessorKey: "statusCode",
			header: t("org-admin.sub-contractors.table.columns.status"),
			enableSorting: false,
			minSize: 140,
			cell: ({ row }) => {
				const closed = isClosedSubContractor(row.original.statusCode);
				return (
					<BadgeWithDot
						type="pill-color"
						size="sm"
						color={closed ? "gray" : "success"}
					>
						{closed
							? t("org-admin.sub-contractors.status.closed")
							: t("org-admin.sub-contractors.status.active")}
					</BadgeWithDot>
				);
			},
		},
		{
			id: "address",
			accessorKey: "address",
			header: t("org-admin.sub-contractors.table.columns.location"),
			enableSorting: true,
			minSize: 300,
			cell: ({ row }) => {
				return row.original.address ? (
					<TableAddressDisplay address={row.original.address} />
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "phone",
			accessorKey: "phone",
			header: t("org-admin.sub-contractors.table.columns.phone"),
			enableSorting: true,
			minSize: 150,
			cell: ({ row }) => {
				return row.original.phone ? (
					<PhoneNumber phoneNumber={row.original.phone} />
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "totalAssignedUserCount",
			accessorKey: "totalAssignedUserCount",
			header: t("org-admin.sub-contractors.table.columns.userCount"),
			enableSorting: true,
			minSize: 155,
			cell: ({ row }) => {
				return row.original.totalAssignedUserCount !== undefined &&
					row.original.totalAssignedUserCount !== null ? (
					<span className="text-sm font-medium">
						{row.original.totalAssignedUserCount}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "created",
			accessorKey: "created",
			header: t("org-admin.sub-contractors.table.columns.created"),
			enableSorting: true,
			minSize: 175,
			cell: ({ row }) => {
				return row.original.created ? (
					<span className="text-sm font-medium">
						{formatLongDate(row.original.created)}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "lastUpdated",
			accessorKey: "lastUpdated",
			header: t("org-admin.sub-contractors.table.columns.lastUpdate"),
			enableSorting: true,
			minSize: 175,
			cell: ({ row }) => {
				return row.original.lastUpdated ? (
					<span className="text-sm font-medium">
						{formatLongDate(row.original.lastUpdated)}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "id",
			accessorKey: "id",
			header: t("org-admin.sub-organizations.table.columns.id"),
			enableSorting: true,
			cell: ({ row }) => {
				return row.original.id ? (
					<div className="inline-flex min-w-0 max-w-full">
						<span className="text-sm font-medium">
							<CopyableId value={row.original.id} />
						</span>
					</div>
				) : (
					<EmptyValue />
				);
			},
		},
	];

	const noSubContractors = (isSearchEmpty: boolean) => {
		if (isSearchEmpty) {
			return (
				<TableSearchEmptyState titleKey="org-admin.sub-contractors.table.empty.noSearchResults" />
			);
		}

		return (
			<div className="flex flex-col items-center justify-center">
				<div className="relative flex items-center justify-center w-[480px] h-[480px]">
					<div className="relative flex flex-col items-center justify-center">
						<Illustration type="cloud" size="sm">
							<GitMerge className="size-6" />
						</Illustration>
						<div className="mt-4 text-center">
							<h2 className="font-semibold text-primary text-md">
								{t("org-admin.sub-contractors.table.empty.title")}
							</h2>
							<p className="text-sm text-tertiary">
								{t("org-admin.sub-contractors.table.empty.description")}
							</p>
						</div>
						{onCreateClick ? (
							<Button
								size="sm"
								color="primary"
								onClick={onCreateClick}
								className="mt-4"
							>
								{t("org-admin.sub-contractors.table.createSubContractor")}
							</Button>
						) : null}
					</div>
				</div>
			</div>
		);
	};

	// Prefetch adjacent pages to eliminate loading delays
	useEffect(() => {
		// Don't prefetch if we don't have a page count yet
		if (!pageCount) return;

		// Configure how many pages ahead/behind to prefetch
		const prefetchRange = 1;

		// Prefetch multiple pages ahead
		for (let i = 1; i <= prefetchRange; i++) {
			const nextPageIndex = pageIndex + i;
			if (nextPageIndex < pageCount) {
				const nextStartIndex = nextPageIndex * pageSize + 1;
				queryClient.prefetchQuery(
					organizationQueryOptions.subContractors.list(
						params.orgId,
						nextStartIndex,
						pageSize,
						{
							SearchTerm: searchTerm,
							...filterParams,
							...statusParams,
							...sortParams,
						},
					),
				);
			}
		}

		// Prefetch multiple pages behind
		for (let i = 1; i <= prefetchRange; i++) {
			const prevPageIndex = pageIndex - i;
			if (prevPageIndex >= 0) {
				const prevStartIndex = prevPageIndex * pageSize + 1;
				queryClient.prefetchQuery(
					organizationQueryOptions.subContractors.list(
						params.orgId,
						prevStartIndex,
						pageSize,
						{
							SearchTerm: searchTerm,
							...filterParams,
							...statusParams,
							...sortParams,
						},
					),
				);
			}
		}
	}, [
		pageIndex,
		pageSize,
		pageCount,
		searchTerm,
		filterParams,
		statusParams,
		sortParams,
		queryClient,
		params.orgId,
	]);

	// Handle error state
	if (allSubContractors.isError) {
		return (
			<div className="space-y-0">
				<div className="flex flex-col items-center justify-center py-12">
					<div className="text-center">
						<h3 className="text-sm font-medium text-error">
							{t(
								"org-admin.sub-contractors.table.errors.failedToLoadSubContractors",
							)}
						</h3>
						<p className="text-sm text-tertiary mt-1">
							{allSubContractors.error?.message}
						</p>
						<Button
							size="sm"
							color="primary"
							onClick={handleRefresh}
							className="mt-3"
						>
							{t("common.actions.tryAgain")}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<>
			<PageLayout
				header={
					<PageHeader
						type="landing"
						title={t("org-admin.sub-contractors.table.title")}
						description={t("org-admin.sub-contractors.table.description")}
						actions={
							data.length > 0 && onCreateClick
								? [
										{
											label: t(
												"org-admin.sub-contractors.table.createSubContractor",
											),
											color: "accent" as const,
											onPress: onCreateClick,
										},
									]
								: undefined
						}
						overflowControl={<div ref={setTableOptionsHost} />}
						overflowAriaLabel={t("common.tableActions.options")}
					/>
				}
			>
				<CustomTableWrapper
					tableKey={TABLE_KEY}
					// Data
					data={data}
					columns={subContractorColumns}
					// Display
					title={t("org-admin.sub-contractors.table.title")}
					showHeaderPadding={false}
					showCard={true}
					zebraStriping
					// Loading States
					isLoading={allSubContractors.isLoading}
					isFetching={allSubContractors.isFetching}
					isRefreshing={isRefreshing}
					onRefresh={handleRefresh}
					// Pagination
					paginationMode="server"
					externalPaginationState={{ pageSize, pageIndex }}
					onPaginationChange={handlePaginationChange}
					pageCount={pageCount}
					rowCount={_subContractorCount.data}
					totalRowCount={unFilteredSubContractorCount.data}
					// Sorting
					sortFieldMapping={sortFieldMapping}
					defaultSortField="name"
					onSortChange={({ fields, descending }) => {
						setSortParams({
							"OrderBy.Fields": fields,
							"OrderBy.Descending": descending,
						});
					}}
					// Filtering
					filterPlaceholder={t(
						"org-admin.sub-contractors.table.search.placeholder",
					)}
					filterValue={searchTerm}
					onDebouncedFilterChange={handleDebouncedSearchChange}
					filtersTrailingContent={
						<SubOrgTableFilters value={filters} onChange={handleFilterChange} />
					}
					// Table Actions
					enableTableActions
					filterLayout="bar"
					filterBarType="landing"
					filterBarClassName="pt-0"
					tableOptionsPortalTarget={tableOptionsHost}
					hasActiveFilters={
						Object.keys(filterParams).length > 0 ||
						statusFilter !== SubContractorStatusFilter.Active
					}
					onExportClick={handleExport}
					isExportLoading={exportMutation.isPending}
					// Features
					enableColumnReordering
					onRowClick={handleRowClick}
					enableColumnResizing
					// Persistence - Initial values from saved settings
					initialSortingState={tableSettings.sorting}
					initialColumnOrder={normalizeColumnOrder(tableSettings.columnOrder)}
					initialColumnVisibility={tableSettings.columnVisibility}
					settingsLoaded={settingsLoaded}
					// Persistence - Save callbacks
					onSortingStateChange={saveSorting}
					onColumnOrderChange={(order) => {
						saveColumnOrder(normalizeColumnOrder(order));
					}}
					onColumnVisibilityChange={saveColumnVisibility}
				/>

				{!allSubContractors.isLoading &&
				!allSubContractors.isFetching &&
				data.length <= 0
					? noSubContractors(
							(unFilteredSubContractorCount.data ?? 0) > 0 &&
								(_subContractorCount.data ?? 0) === 0,
						)
					: null}
			</PageLayout>
			<Outlet />
		</>
	);
}
